import React, { useMemo } from 'react';
import { Card, Empty, Tag } from 'antd';
import dayjs from 'dayjs';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const EXPENSE_LABELS = {
  fbaStorageFee: 'FBA Storage Fee',
  inboundFee: 'FBA Inbound Fee',
  refund: 'Refund',
  sellingFee: 'Selling Fee',
  fbaFee: 'FBA Fee',
  amazonAdsCost: 'AMZ Ads Cost',
  dspAdsCost: 'DSP Ads Cost',
  promotion: 'Promotion',
};

const money = value => Number(value || 0).toLocaleString('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
});
const compactMoney = value => {
  const number = Number(value || 0);
  if (Math.abs(number) >= 1000000) return `$${(number / 1000000).toFixed(1)}M`;
  if (Math.abs(number) >= 1000) return `$${(number / 1000).toFixed(0)}k`;
  return `$${number.toFixed(0)}`;
};

const ChartEmpty = () => <div className="profit-chart-empty"><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có dữ liệu biểu đồ" /></div>;

const ExpenseTooltip = ({ active, payload, label, coverage }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="profit-chart-tooltip">
      <strong>{label}</strong>
      <div><span>Income</span><b>{money(row.revenue)}</b></div>
      <div><span>Tổng chi phí</span><b>{money(row.expenses)}</b></div>
      <div><span>% Net Margin</span><b>{Number(row.netMarginPercent || 0).toFixed(2)}%</b></div>
      <div className="profit-chart-tooltip-divider" />
      {Object.entries(EXPENSE_LABELS).map(([key, text]) => {
        const unavailable = (key === 'amazonAdsCost' && coverage.amazonAdsCost === false) ||
          (key === 'dspAdsCost' && coverage.dspAdsCost === false);
        return <div key={key}><span>{text}</span><b>{unavailable ? '—' : money(row.expenseBreakdown?.[key])}</b></div>;
      })}
    </div>
  );
};

const ProfitCharts = ({ charts, reportEndDate, accountRows = [], showComparison = true, coverage = {} }) => {
  const granularity = charts?.granularity || 'day';
  const timeSeries = useMemo(() => {
    const previous = charts?.previous || [];
    return (charts?.current || []).map((row, index) => ({
      ...row,
      label: granularity === 'week'
        ? `Tuần ${dayjs(row.bucketStart).format('DD/MM')}`
        : dayjs(row.bucketStart).format('DD/MM'),
      previousRevenue: previous[index]?.revenue,
      previousNetProfit: previous[index]?.netProfit,
      previousExpenses: previous[index]?.expenses,
      previousNetMarginPercent: previous[index]?.netMarginPercent,
    }));
  }, [charts, granularity]);

  const topAccounts = useMemo(() => [...accountRows]
    .sort((left, right) => Number(right.totalRevenue || 0) - Number(left.totalRevenue || 0))
    .slice(0, 10)
    .map(row => ({
      name: row.name,
      revenue: Number(row.totalRevenue || 0),
      netProfit: Number(row.netMargin || 0),
    })), [accountRows]);

  const trendSeries = useMemo(() => {
    const viewerToday = dayjs().format('YYYY-MM-DD');
    const shouldHideOpenDay = granularity === 'day' && reportEndDate === viewerToday;
    return shouldHideOpenDay
      ? timeSeries.filter(row => row.bucketStart !== reportEndDate)
      : timeSeries;
  }, [granularity, reportEndDate, timeSeries]);

  return (
    <section className="profit-report-charts">
      <div className="profit-report-chart-grid">
        <Card
          className="profit-report-chart-card"
          title="Revenue & Net Profit Trend"
          extra={<Tag>{granularity === 'day' ? 'Theo ngày' : 'Theo tuần'}</Tag>}
        >
          {!trendSeries.length ? <ChartEmpty /> : (
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={trendSeries} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#edf1f6" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#718096' }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={compactMoney} tick={{ fontSize: 10, fill: '#718096' }} axisLine={false} tickLine={false} width={48} />
                <Tooltip formatter={(value, name) => [money(value), name]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#2563eb" strokeWidth={2.4} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="netProfit" name="Net Profit" stroke="#10a777" strokeWidth={2.4} dot={{ r: 3 }} />
                {showComparison && <Line type="monotone" dataKey="previousRevenue" name="Revenue cùng kỳ" stroke="#93b4ff" strokeDasharray="5 4" dot={false} connectNulls />}
                {showComparison && <Line type="monotone" dataKey="previousNetProfit" name="Net Profit cùng kỳ" stroke="#8bd8bd" strokeDasharray="5 4" dot={false} connectNulls />}
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="profit-report-chart-card" title="Top Revenue Accounts">
          {!topAccounts.length ? <ChartEmpty /> : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={topAccounts} layout="vertical" margin={{ top: 8, right: 12, left: 12, bottom: 0 }}>
                <CartesianGrid stroke="#edf1f6" horizontal={false} />
                <XAxis type="number" tickFormatter={compactMoney} tick={{ fontSize: 10, fill: '#718096' }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" width={92} tick={{ fontSize: 10, fill: '#52617a' }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(value, name) => [money(value), name]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="revenue" name="Revenue" fill="#18b7c9" radius={[0, 4, 4, 0]} />
                <Bar dataKey="netProfit" name="Net Profit" fill="#10a777" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="profit-report-chart-card is-wide" title="Income, Expenses & % Net Margin">
          {!timeSeries.length ? <ChartEmpty /> : (
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={timeSeries} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#edf1f6" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#718096' }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="money" tickFormatter={compactMoney} tick={{ fontSize: 10, fill: '#718096' }} axisLine={false} tickLine={false} width={48} />
                <YAxis yAxisId="percent" orientation="right" tickFormatter={value => `${value}%`} tick={{ fontSize: 10, fill: '#059669' }} axisLine={false} tickLine={false} width={42} />
                <Tooltip content={<ExpenseTooltip coverage={coverage} />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="money" dataKey="revenue" name="Income" fill="#2563eb" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="money" dataKey="expenses" name="Expenses" fill="#ef4444" radius={[4, 4, 0, 0]} />
                {showComparison && <Bar yAxisId="money" dataKey="previousRevenue" name="Income cùng kỳ" fill="#93c5fd" radius={[4, 4, 0, 0]} />}
                {showComparison && <Bar yAxisId="money" dataKey="previousExpenses" name="Expenses cùng kỳ" fill="#fca5a5" radius={[4, 4, 0, 0]} />}
                <Line yAxisId="percent" type="monotone" dataKey="netMarginPercent" name="% Net Margin" stroke="#059669" strokeWidth={2.8} dot={{ r: 3, fill: '#fff', strokeWidth: 2 }} />
                {showComparison && <Line yAxisId="percent" type="monotone" dataKey="previousNetMarginPercent" name="% Margin cùng kỳ" stroke="#6ee7b7" strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls />}
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>
    </section>
  );
};

export default ProfitCharts;
